/**
 * @lodex-pro/shared/printing/escpos-commands — ESC/POS command bytes + helpers.
 *
 * Phase 10 / Task 10.3. Pure, dependency-free building blocks for the run-ticket
 * and disposal-ticket templates. Zero side effects, zero SDK imports, zero I/O —
 * every export is a `Uint8Array` constant, a `Uint8Array`-returning helper, or a
 * pure string formatter. Safe to import from api / mobile / web / Node tests.
 *
 * Two media widths are supported (see {@link PaperWidth}). 80mm remains the default
 * everywhere, so nothing changes for an existing printer until a narrow one is
 * configured.
 */

/** Printable columns on 80mm media in Font A (12 dots/char over 576 dots). */
export const PRINTABLE_COLUMNS = 48;

// ─── Media width ──────────────────────────────────────────────────────────────

/**
 * Which media a given printer is loaded with.
 *
 * A PROPERTY OF THE PRINTER, NOT THE TENANT. A hauler runs an 80mm Epson in the office
 * and 58mm NETUMs in the trucks, so this is stored per remembered printer; a tenant-level
 * setting would be wrong for one of them by construction.
 */
export type PaperWidth = 'WIDE_80MM' | 'NARROW_58MM';

export interface PaperProfile {
  readonly width: PaperWidth;
  /** Font A columns across the printable area. */
  readonly columns: number;
  /** Printable dots across — the hard limit for barcodes and raster images. */
  readonly dots: number;
  /**
   * Columns available in FONT_LARGE.
   *
   * FONT_LARGE is double-width, so this is exactly half of {@link columns} — it is NOT a
   * separate tunable. It used to be a hardcoded 24 duplicated across the three ticket
   * builders, which silently meant "80mm" and overflowed narrow media.
   */
  readonly largeColumns: number;
}

const WIDE: PaperProfile = { width: 'WIDE_80MM', columns: 48, dots: 576, largeColumns: 24 };
/** 58mm media: 48mm printable = 384 dots = 32 Font A columns at 203 dpi. */
const NARROW: PaperProfile = { width: 'NARROW_58MM', columns: 32, dots: 384, largeColumns: 16 };

/** The profile used when a printer has no configured width — unchanged 80mm behaviour. */
export const DEFAULT_PAPER: PaperProfile = WIDE;

/** Resolve a {@link PaperWidth} (or nothing) to its profile. */
export function paperProfile(width?: PaperWidth | null): PaperProfile {
  return width === 'NARROW_58MM' ? NARROW : WIDE;
}

// ─── Command constants ────────────────────────────────────────────────────────

/** Initialize printer (`ESC @`) — resets to a known state; every ticket starts here. */
export const INIT = new Uint8Array([0x1b, 0x40]);
/** Single line feed (`LF`). */
export const FEED_LINE = new Uint8Array([0x0a]);
/** Feed `n` lines (`ESC d n`). Used to clear the tear bar at the end of a ticket. */
export const FEED_N_LINES = (n: number): Uint8Array => new Uint8Array([0x1b, 0x64, n & 0xff]);

/** Partial cut (`GS V 1`) — severs all but a narrow strip, so the ticket stays on the roll. */
export const CUT_PARTIAL = new Uint8Array([0x1d, 0x56, 0x01]);
/** Full cut (`GS V 0`) — severs completely. Not used: the ticket would drop to the ground. */
export const CUT_FULL = new Uint8Array([0x1d, 0x56, 0x00]);

// ─── Printer capabilities ─────────────────────────────────────────────────────

/**
 * What the unit in the driver's hand can physically do — a property of the PRINTER, like
 * {@link PaperWidth}, and for the same reason: one hauler's fleet is not one model.
 */
export interface PrinterCapabilities {
  /**
   * This printer has an auto-cutter, so tickets end with a partial cut (DEC-145).
   *
   * DEFAULTS TO FALSE, DELIBERATELY (DEC-279). DEC-145 assumed "a printer genuinely
   * lacking a cutter simply ignores the cut byte", which would make an unconditional cut
   * safe everywhere. That is an assumption, not an observation, and it is wrong about the
   * one printer that prints real tickets today: the Bixolon SPP-R310's command manual
   * (Rev. 1.00) enumerates its 63 supported commands and `GS V` is not among them — nor
   * are `ESC i` or `ESC m`, the other two cut commands. `GS V` appears in that document
   * only as a cross-reference inside `GS ( F`'s remarks. An ESC/POS printer's response to
   * a command outside its set is firmware-defined; the realistic failure is a stray `V`
   * printed at the foot of a compliance document.
   *
   * Nor does the model name settle it. The TM-P80II ships in auto-cutter and
   * non-auto-cutter variants, exactly as "SPP-R310" covers Bluetooth-only and WLAN-only
   * units (DEC-193). Cutter-ness belongs to the unit, so it is configured per printer and
   * starts off: an un-ticked box prints what the fleet prints today, byte for byte.
   */
  readonly hasCutter?: boolean;
}

/**
 * The end of every ticket: feed `lines` to carry the ticket clear of the print head, then
 * a PARTIAL cut if — and only if — this printer has a cutter (DEC-145, gated by DEC-279).
 *
 * One helper for all three templates so they cannot drift on whether, or where, they cut.
 * The feed comes FIRST and is unchanged by the gate: on a cutter-equipped unit it is what
 * advances the printed ticket to the blade (cut first and you sever blank paper above the
 * signature); on a tear-off unit it is what clears the tear bar, as it always has.
 */
export function ticketEnd(lines: number, printer?: PrinterCapabilities): Uint8Array {
  const feed = FEED_N_LINES(lines);
  return printer?.hasCutter === true ? concat(feed, CUT_PARTIAL) : feed;
}

// Text alignment (`ESC a n`).
export const ALIGN_LEFT = new Uint8Array([0x1b, 0x61, 0x00]);
export const ALIGN_CENTER = new Uint8Array([0x1b, 0x61, 0x01]);
export const ALIGN_RIGHT = new Uint8Array([0x1b, 0x61, 0x02]);

// Emphasis (`ESC E n`) + underline (`ESC - n`).
export const BOLD_ON = new Uint8Array([0x1b, 0x45, 0x01]);
export const BOLD_OFF = new Uint8Array([0x1b, 0x45, 0x00]);
export const UNDERLINE_ON = new Uint8Array([0x1b, 0x2d, 0x01]);
export const UNDERLINE_OFF = new Uint8Array([0x1b, 0x2d, 0x00]);
// Reverse video / inverted band (`GS B n`) — white-on-black (Task 10.7 provisional banner).
export const REVERSE_ON = new Uint8Array([0x1d, 0x42, 0x01]);
export const REVERSE_OFF = new Uint8Array([0x1d, 0x42, 0x00]);

// Font size (`ESC ! n`).
export const FONT_NORMAL = new Uint8Array([0x1b, 0x21, 0x00]);
/** Double width (halves the effective column count to 24). */
export const FONT_WIDE = new Uint8Array([0x1b, 0x21, 0x20]);
/** Double height. */
export const FONT_TALL = new Uint8Array([0x1b, 0x21, 0x10]);
/** Double width + height (24 effective columns). */
export const FONT_LARGE = new Uint8Array([0x1b, 0x21, 0x30]);

/** Select the USA character set (`ESC R 0`). */
export const CHARSET_USA = new Uint8Array([0x1b, 0x52, 0x00]);

// ─── Byte helpers ─────────────────────────────────────────────────────────────

/** Concatenate multiple `Uint8Array`s into one. */
export function concat(...arrays: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const a of arrays) total += a.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    out.set(a, offset);
    offset += a.length;
  }
  return out;
}

// ─── Printable text (OPEN-118) ────────────────────────────────────────────────

/**
 * What a non-ASCII character prints as. Keyed by CODE POINT, not by a literal character,
 * so this table has no invisible characters in it (nbsp, zero-width space, BOM) for a
 * reader or a linter to trip over. An empty string drops the character.
 *
 * Why ASCII only, and not Latin-1. The templates send `ESC @` and `ESC R 0` and NEVER a
 * code table (`ESC t`), so bytes 0x80-0xFF print from whatever table the unit defaults to.
 * On Epson/Bixolon that is PC437, where Latin-1's 0xE9 (e-acute) is a Greek theta and
 * 0xF1 (n-tilde) is a plus-minus sign; Chinese-market units (the NETUMs) can default to a
 * double-byte table where a high byte also swallows the character after it. Only
 * 0x20-0x7E is the same on every unit, so that is all this module ever sends as text.
 */
const PRINTABLE_SUBSTITUTES: ReadonlyMap<number, string> = new Map<number, string>([
  // Latin-1 punctuation and symbols (0xA0-0xBF).
  [0xa0, ' '], // no-break space
  [0xa1, '!'],
  [0xa2, 'c'],
  [0xa3, 'L'],
  [0xa5, 'Y'],
  [0xa6, '|'],
  [0xa7, 'S'],
  [0xa8, '"'],
  [0xa9, '(C)'],
  [0xaa, 'a'],
  [0xab, '<<'],
  [0xac, '-'],
  [0xad, ''], // soft hyphen
  [0xae, '(R)'],
  [0xaf, '-'],
  [0xb0, 'deg'], // degree sign: "60degF"
  [0xb1, '+/-'],
  [0xb2, '2'],
  [0xb3, '3'],
  [0xb4, "'"],
  [0xb5, 'u'],
  [0xb6, 'P'],
  [0xb7, '.'],
  [0xb8, ','],
  [0xb9, '1'],
  [0xba, 'o'],
  [0xbb, '>>'],
  [0xbc, '1/4'],
  [0xbd, '1/2'],
  [0xbe, '3/4'],
  // General Punctuation.
  [0x2010, '-'], // hyphen
  [0x2011, '-'], // non-breaking hyphen
  [0x2012, '-'], // figure dash
  [0x2013, '-'], // en dash
  [0x2014, '-'], // em dash: the mobile mappers' "no value" fallback (#689)
  [0x2015, '-'], // horizontal bar
  [0x2212, '-'], // minus sign
  [0x2018, "'"],
  [0x2019, "'"], // right single quote: iOS "smart punctuation" apostrophe
  [0x201a, "'"],
  [0x201b, "'"],
  [0x2032, "'"], // prime: 5'
  [0x201c, '"'],
  [0x201d, '"'],
  [0x201e, '"'],
  [0x201f, '"'],
  [0x2033, '"'], // double prime: 3"
  [0x2039, '<'],
  [0x203a, '>'],
  [0x2022, '*'], // bullet
  [0x2026, '...'], // ellipsis
  [0x20ac, 'EUR'],
  [0x2116, 'No'],
  [0x2122, 'TM'],
  [0x2002, ' '],
  [0x2003, ' '],
  [0x2004, ' '],
  [0x2005, ' '],
  [0x2006, ' '],
  [0x2007, ' '],
  [0x2008, ' '],
  [0x2009, ' '],
  [0x200a, ' '],
  [0x2028, ' '], // line separator
  [0x2029, ' '], // paragraph separator
  [0x202f, ' '], // narrow no-break space
  [0x205f, ' '],
  [0x3000, ' '],
  [0x200b, ''], // zero-width space
  [0x200c, ''],
  [0x200d, ''],
  [0x2060, ''],
  [0xfeff, ''], // BOM / zero-width no-break space
]);

/**
 * Latin-1 letters 0xC0-0xFF folded to their base letter, one entry per code point. The
 * two symbols in the block, 0xD7 (multiplication) and 0xF7 (division), print as `x` and `/`.
 */
const LATIN1_LETTERS: readonly string[] = [
  ...['A', 'A', 'A', 'A', 'A', 'A', 'AE', 'C', 'E', 'E', 'E', 'E', 'I', 'I', 'I', 'I'], // C0-CF
  ...['D', 'N', 'O', 'O', 'O', 'O', 'O', 'x', 'O', 'U', 'U', 'U', 'U', 'Y', 'TH', 'ss'], // D0-DF
  ...['a', 'a', 'a', 'a', 'a', 'a', 'ae', 'c', 'e', 'e', 'e', 'e', 'i', 'i', 'i', 'i'], // E0-EF
  ...['d', 'n', 'o', 'o', 'o', 'o', 'o', '/', 'o', 'u', 'u', 'u', 'u', 'y', 'th', 'y'], // F0-FF
];

/**
 * OPEN-118 — fold `text` to what the printer can actually print: printable ASCII
 * (0x20-0x7E), the same on every unit's code table.
 *
 * - Dashes, curly quotes, primes, ellipsis, bullets: transliterated (`-`, `'`, `"`, `...`).
 * - Every Unicode space (nbsp, thin, ideographic, line separator): a plain space.
 *   Zero-width characters and the soft hyphen: dropped.
 * - Latin-1 letters: folded to the base letter (`E`-acute to `E`, `n`-tilde to `n`).
 * - Tab, CR, LF: a space. A note pasted with a line break must not emit a bare LF in the
 *   middle of a laid-out line, and must not join two words into one.
 * - Any other control character, and anything still unmapped (emoji, CJK, other
 *   scripts): `?`. Visible on paper, never a raw byte the printer would act on — `ESC`
 *   (0x1B) inside a driver note would otherwise be sent as a COMMAND.
 *
 * Idempotent, and the output can be LONGER than the input (`...`, `1/2`), so every text
 * helper below folds FIRST and measures the folded string: a width computed on the
 * unfolded string would let `...` push the right-hand value off the line.
 */
export function toPrintable(text: string): string {
  let out = '';
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0x3f;
    if (code >= 0x20 && code <= 0x7e) {
      out += ch;
    } else if (code === 0x09 || code === 0x0a || code === 0x0d) {
      out += ' ';
    } else if (code >= 0xc0 && code <= 0xff) {
      out += LATIN1_LETTERS[code - 0xc0] ?? '?';
    } else {
      out += PRINTABLE_SUBSTITUTES.get(code) ?? '?';
    }
  }
  return out;
}

/**
 * Encode a single logical line to printer bytes. The text is folded by
 * {@link toPrintable} first, so every byte is printable ASCII (0x20-0x7E) and reads the
 * same on any code table. The line is hard-truncated to {@link PRINTABLE_COLUMNS} (48)
 * so nothing overflows the media width. Does NOT append a newline (use {@link line}).
 */
export function encodeText(text: string, width: number = PRINTABLE_COLUMNS): Uint8Array {
  const printable = toPrintable(text).slice(0, Math.max(0, width));
  const out = new Uint8Array(printable.length);
  for (let i = 0; i < printable.length; i++) out[i] = printable.charCodeAt(i);
  return out;
}

/** A full text line: encoded text followed by a line feed. */
export function line(text: string, width: number = PRINTABLE_COLUMNS): Uint8Array {
  return concat(encodeText(text, width), FEED_LINE);
}

/**
 * A prominent labelled value that NEVER loses the number.
 *
 * Quick 260920-tfc. `leftRight` truncated the RIGHT side when a line overflowed
 * (`r.slice(0, avail)`), which is exactly backwards for a headline figure: the label is
 * redundant, the number is the point. At 58mm `FONT_LARGE` gets only 16 columns, so
 * `leftRight('BBL DELIVERED:', '220', 16)` rendered **"BBL DELIVERED: 2"** — a facility
 * receipt stating 2 barrels for a 220-barrel load. (OPEN-051 has since made `leftRight`
 * lossless, but its overflow layout is not this one: here the label drops to NORMAL width
 * so the figure keeps the large line.)
 *
 * When the pair fits on one double-width line it is emitted as before. When it does not,
 * the label drops to its own NORMAL-width line and the value gets the large line to
 * itself. That keeps the figure prominent and, crucially, complete.
 *
 * Returns the byte runs to push, including the FONT_LARGE/FONT_NORMAL switches.
 */
export function largeLabeledValue(
  label: string,
  value: string,
  width: number,
  largeWidth: number,
): Uint8Array[] {
  const l = toPrintable(label);
  const v = toPrintable(value);
  if (l.length + v.length + 1 <= largeWidth) {
    return [FONT_LARGE, leftRight(l, v, largeWidth), FONT_NORMAL];
  }
  return [line(l, width), FONT_LARGE, line(v, largeWidth), FONT_NORMAL];
}

/**
 * OPEN-051 — the continuation marker.
 *
 * A single `>` at the end of a line that CONTINUES on the next one. The point is that a
 * shortened value must never again be indistinguishable from a complete one: the old
 * failure was silent, so a 38-character seal number printed as a well-formed 32-column
 * line that passed every width check and said nothing about what was missing.
 *
 * ASCII, one column, no UTF-8. An ellipsis character is not safely printable on every
 * ESC/POS code page (the printer substitutes from its own table), and `...` costs three
 * of the 32 columns that are already the scarce resource. `>` is in every code page and
 * reads as "continues" without needing a legend on the ticket.
 */
export const CONTINUATION_MARKER = '>';

/** Indent applied to every continuation line, so wrapped text is visibly subordinate. */
const CONTINUATION_INDENT = '  ';

/**
 * Lay a value across one or more lines, marking every line that continues.
 *
 * `firstWidth` and `contWidth` differ because the first line often shares space with a
 * label while continuation lines are indented. A line is only marked when more text
 * actually follows — the final line never carries a marker, so `>` means exactly one
 * thing.
 */
function layoutValue(
  value: string,
  firstWidth: number,
  contWidth: number,
  marker: string = CONTINUATION_MARKER,
): string[] {
  const out: string[] = [];
  let i = 0;
  let w = firstWidth;
  while (i < value.length) {
    const rest = value.length - i;
    if (rest <= w) {
      out.push(value.slice(i));
      break;
    }
    // Reserve a column for the marker. Math.max(1, …) keeps this terminating even at a
    // pathological width — an infinite loop here would hang the print path, not just
    // render badly.
    const take = Math.max(1, w - marker.length);
    out.push(value.slice(i, i + take) + marker);
    i += take;
    w = contWidth;
  }
  return out;
}

/**
 * WRAP — for NAMES (facility, operator).
 *
 * `FACILITY: CIMARRON RIDGE SALT>` / `  WATER DISPOSAL #14`. The value starts on the
 * label's line and flows onto indented continuation lines. Chosen for names because they
 * are read as prose: a reader reassembles a wrapped company name without effort, and
 * keeping the first words beside the label preserves the scan-down-the-labels reading
 * that the two-column layout exists for.
 *
 * Falls back to {@link labeledStack} when the label leaves too little room to be worth
 * sharing — otherwise the first line carries two or three characters and the wrap reads
 * as noise.
 */
export function labeledWrap(
  rawLabel: string,
  rawValue: string,
  width: number = PRINTABLE_COLUMNS,
): Uint8Array[] {
  const label = toPrintable(rawLabel);
  const value = toPrintable(rawValue);
  if (label.length + 1 + value.length <= width) {
    return [leftRight(label, value, width)];
  }
  const head = `${label} `;
  const firstWidth = width - head.length;
  const contWidth = width - CONTINUATION_INDENT.length;
  // Below this the label is eating the line; stacking gives the value the full width.
  const MIN_SHARED = 4;
  if (firstWidth < MIN_SHARED || contWidth < MIN_SHARED) {
    return labeledStack(label, value, width);
  }
  const pieces = layoutValue(value, firstWidth, contWidth);
  return pieces.map((piece, idx) =>
    idx === 0 ? line(head + piece, width) : line(CONTINUATION_INDENT + piece, width),
  );
}

/**
 * STACK — for IDENTIFIERS (seal number, reference / lease).
 *
 * `SEAL NO:` on its own line, then the value indented beneath it. Identifiers are read
 * character by character and transcribed, so they get the full column budget rather than
 * whatever the label leaves over; splitting one across a label boundary invites a reader
 * to mistake the two halves for separate fields. The value still wraps with the same
 * marker when it exceeds even the full width.
 */
export function labeledStack(
  rawLabel: string,
  rawValue: string,
  width: number = PRINTABLE_COLUMNS,
): Uint8Array[] {
  const label = toPrintable(rawLabel);
  const value = toPrintable(rawValue);
  if (label.length + 1 + value.length <= width) {
    return [leftRight(label, value, width)];
  }
  const contWidth = Math.max(1, width - CONTINUATION_INDENT.length);
  const pieces = layoutValue(value, contWidth, contWidth);
  return [line(label, width), ...pieces.map((piece) => line(CONTINUATION_INDENT + piece, width))];
}

/**
 * A two-column line: `left` flush-left, `right` flush-right, padded with spaces between.
 * Emits a trailing line feed — and, when the pair overflows, more than one line.
 *
 * OPEN-051 — it NEVER drops a character. It used to resolve an overflow by cutting the
 * right side (`r.slice(0, avail)`), which on a 58mm service-charge line printed
 * `SATURDAY WASHOUT SURCHARGE FEE 2` for 220 bbl: the long half was the LEFT one, so the
 * quantity was what got cut. The templates now pick WRAP or STACK for every free-text
 * field, so this path is the backstop for whatever they leave on a plain pair: a pair
 * that fits is byte-for-byte what it always was, and one that does not goes to
 * {@link overflowPair}.
 */
export function leftRight(
  left: string,
  right: string,
  width: number = PRINTABLE_COLUMNS,
): Uint8Array {
  const l = toPrintable(left);
  const r = toPrintable(right);
  if (l.length + 1 + r.length <= width) {
    return line(l + ' '.repeat(width - l.length - r.length) + r, width);
  }
  return concat(...overflowPair(l, r, width));
}

/**
 * The overflow layout behind {@link leftRight}, for pairs whose LEFT side may be the long
 * one (a service-charge name). The label prints whole, laid out with the continuation
 * marker if it exceeds the line. The value then goes flush-right beside the label's last
 * line when it fits there — keeping the number at the right edge where the two-column
 * layout puts every number — or flush-right on a line of its own, or, only if it is
 * wider than the paper, stacked with markers like {@link labeledStack}.
 *
 * Both arguments are already folded by {@link toPrintable}.
 */
function overflowPair(label: string, value: string, width: number): Uint8Array[] {
  const contWidth = Math.max(1, width - CONTINUATION_INDENT.length);
  const labelLines = layoutValue(label, width, contWidth).map((piece, idx) =>
    idx === 0 ? piece : CONTINUATION_INDENT + piece,
  );
  const last = labelLines[labelLines.length - 1] ?? '';
  if (labelLines.length > 0 && last.length + 1 + value.length <= width) {
    const beside = last + ' '.repeat(width - last.length - value.length) + value;
    return [...labelLines.slice(0, -1), beside].map((l) => line(l, width));
  }
  const valueLines =
    value.length <= width
      ? [' '.repeat(width - value.length) + value]
      : layoutValue(value, contWidth, contWidth).map((piece) => CONTINUATION_INDENT + piece);
  return [...labelLines, ...valueLines].map((l) => line(l, width));
}

/** A full-width divider line of `char` (default `-`), with a trailing line feed. */
export function divider(char: string = '-', width: number = PRINTABLE_COLUMNS): Uint8Array {
  const c = char.length > 0 ? (char[0] ?? '-') : '-';
  return line(c.repeat(width), width);
}

/**
 * Word-wrap `text` to lines of at most `width` characters. Splits on whitespace;
 * a single word longer than `width` is hard-split. Returns the wrapped lines
 * (no trailing newlines — the caller emits them via {@link line}).
 */
export function wordWrap(text: string, width: number = PRINTABLE_COLUMNS): string[] {
  const words = toPrintable(text)
    .split(/\s+/)
    .filter((w) => w.length > 0);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    let word = w;
    // Hard-split any word that is itself wider than the line.
    while (word.length > width) {
      if (cur !== '') {
        lines.push(cur);
        cur = '';
      }
      lines.push(word.slice(0, width));
      word = word.slice(width);
    }
    if (word.length === 0) continue;
    if (cur === '') {
      cur = word;
    } else if (cur.length + 1 + word.length <= width) {
      cur += ' ' + word;
    } else {
      lines.push(cur);
      cur = word;
    }
  }
  if (cur !== '') lines.push(cur);
  return lines;
}

/**
 * PROSE — ONE PARAGRAPH of fixed ticket copy (a certification statement, a sync notice).
 *
 * The third OPEN-051 treatment, for text that is not a labelled value at all: the
 * certification sentences and sync warnings the templates print centred. Those were string
 * literals PRE-SPLIT into lines that fit 48 columns, and {@link line} hard-truncates, so at
 * 32 each one lost its tail — `'By signing below, facility certi'`,
 * `'OFFICIAL TICKET WILL BE ISSUED U'`. A different mechanism from the one WRAP and STACK
 * fixed (a pre-split literal, not `leftRight`) and the same root cause: a layout decision
 * baked in at authoring time against a single width.
 *
 * `preferredLines` is how the copy is written — the break the author chose, which on 80mm
 * paper is the one that should print. So:
 *
 *   - every preferred line fits `width` -> they are emitted EXACTLY as given. At 48 columns
 *     that is byte-identical to the literals this replaces, so every 80mm ticket in the
 *     field is unchanged. The author's break is a preference, and it is honoured.
 *   - any of them does not fit -> the preference cannot be honoured, so the paragraph is
 *     rejoined and reflowed as ONE piece of prose at the live width. Wrapping each line
 *     independently would instead keep a break that no longer makes sense and strand its
 *     tail on a line of its own.
 *
 * ONE CALL IS ONE PARAGRAPH, because that is the unit the rejoin applies to. Two separate
 * sentences are two calls, or a narrow roll would run them together on a shared line.
 *
 * The reflowed lines CARRY {@link CONTINUATION_MARKER}, as DEC-223 requires — every line
 * but the last. The marker does not disclose a mid-word split here (there is none —
 * {@link wordWrap} breaks on whitespace); it discloses that THE SENTENCE IS NOT OVER. That is
 * the OPEN-051 failure this helper exists for: 'By signing below, facility certi' was a
 * well-formed line that said nothing about what followed, and a clean break at
 * 'By signing below, facility' is well-formed in exactly the same way. On a certification
 * statement a facility representative signs, "there is more above this signature line" is not
 * decoration.
 *
 * A column is therefore reserved for the marker on the wrap path, so a marked line still fits
 * `width`. The exact path reserves nothing: 48-column output never wraps, so it carries no
 * marker and stays byte-identical to the literals it replaced.
 */
export function proseBlock(
  preferredLines: readonly string[],
  width: number = PRINTABLE_COLUMNS,
): Uint8Array[] {
  const printable = preferredLines.map(toPrintable);
  if (printable.every((l) => l.length <= width)) {
    return printable.map((l) => line(l, width));
  }
  // Math.max(1, …) keeps the wrap width positive at a pathological width, the same guard
  // layoutValue carries — a zero width would not terminate.
  const wrapWidth = Math.max(1, width - CONTINUATION_MARKER.length);
  const lines = wordWrap(printable.join(' '), wrapWidth);
  const last = lines.length - 1;
  return lines.map((l, i) => line(i === last ? l : l + CONTINUATION_MARKER, width));
}

/**
 * A Code 128 barcode of `data` with human-readable text (HRI) printed BELOW the
 * bars. Uses `GS k` function 73 (CODE128), code set B (`{B`), preceded by HRI
 * position (`GS H 2`), height (`GS h 80`) and module width (`GS w 2`). Emits a
 * trailing line feed. `data` is expected to be short ASCII (a haul / provisional
 * id); it is folded by {@link toPrintable} (Code 128 set B is ASCII 0x20-0x7F) and
 * truncated defensively.
 */
export function barcode(data: string, dots: number = DEFAULT_PAPER.dots): Uint8Array {
  const payloadData = encodeText(data, PRINTABLE_COLUMNS);
  // Code set B selector `{B` (0x7B 0x42) prefixes the data for GS k function 73.
  const payload = concat(new Uint8Array([0x7b, 0x42]), payloadData);
  const n = payload.length & 0xff; // number of data bytes (incl. the {B selector)
  const module = barcodeModuleWidth(payloadData.length, dots);
  return concat(
    new Uint8Array([0x1d, 0x48, 0x02]), // GS H 2  — HRI text below the barcode
    new Uint8Array([0x1d, 0x68, 0x50]), // GS h 80 — barcode height (dots)
    new Uint8Array([0x1d, 0x77, module]), // GS w n — module width (computed)
    new Uint8Array([0x1d, 0x6b, 0x49, n]), // GS k 73 n — CODE128 (function B)
    payload,
    FEED_LINE,
  );
}

/**
 * Total Code 128 width in MODULES for `chars` data characters.
 *
 * start (11) + data (11 each) + checksum (11) + stop (13).
 */
export function code128Modules(chars: number): number {
  return 11 * (chars + 2) + 13;
}

/**
 * Smallest module width (dots) that is still legible, or `null` when the symbol cannot fit
 * the media at a scannable size.
 *
 * The minimum is 2 dots, NOT 1. At 203 dpi one dot is 0.125mm, roughly half the ~0.25mm
 * X-dimension handheld scanners expect. Shrinking to 1 would produce a barcode that prints
 * and then fails to scan intermittently, in sunlight, on a smudged thermal ticket — a
 * worse failure than not printing, because it fails silently.
 */
export function barcodeModuleWidth(chars: number, dots: number): number {
  const modules = code128Modules(chars);
  return Math.max(MIN_BARCODE_MODULE, Math.min(2, Math.floor(dots / modules)));
}

/** Below this the symbol is not reliably scannable at 203 dpi. */
export const MIN_BARCODE_MODULE = 2;

/**
 * Whether a Code 128 of `chars` characters fits `dots` at a legible module width.
 *
 * Callers use this to choose a QR code instead, rather than emitting a barcode that
 * silently overflows the media — which is exactly what produced a ticket with NO barcode
 * at all on the NT-1809 (422 dots needed, 384 available).
 */
export function barcodeFits(chars: number, dots: number): boolean {
  return code128Modules(chars) * MIN_BARCODE_MODULE <= dots;
}

// ─── Content formatters (DEC-004: device-local, 12-hour AM/PM; never UTC) ──────

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Format a date as `MMM D, YYYY` in the Date's LOCAL fields (DEC-004 — the mobile
 * layer's device timezone; never UTC). Uses local getters (not `toLocaleString`)
 * so it is pure + deterministic in tests regardless of the runner's timezone.
 */
export function formatDate(d: Date): string {
  return `${MONTHS[d.getMonth()] ?? ''} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Format a time as `h:mm AM/PM` in the Date's LOCAL fields (DEC-004; never UTC). */
export function formatTime(d: Date): string {
  const hours24 = d.getHours();
  const ampm = hours24 < 12 ? 'AM' : 'PM';
  const hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hour12}:${minutes} ${ampm}`;
}

/** Format a gauge reading as `Xft Yin` (e.g. `14ft 3in`), or `-` when both absent. */
export function formatGauge(feet?: number, inches?: number): string {
  if (feet === undefined && inches === undefined) return '-';
  return `${feet ?? 0}ft ${inches ?? 0}in`;
}

/** Format a BBL volume with up to 2 decimals (no trailing zeros), or `-` when absent. */
export function formatBbl(bbl?: number): string {
  if (bbl === undefined) return '-';
  return String(Math.round(bbl * 100) / 100);
}

// ─── Raster logo (Task 10.6) ──────────────────────────────────────────────────

/**
 * Pack a 1-bit monochrome bitmap into an ESC/POS raster bit-image (`GS v 0`).
 * Pure — the caller (server-side, with an image library) is responsible for
 * decoding/downscaling/thresholding the source image into `mono`.
 *
 * `mono` is row-major, one entry per pixel: a truthy value = a BLACK (printed)
 * dot, falsy = white. Rows are byte-aligned (8 px/byte, MSB = leftmost pixel),
 * matching the `GS v 0` format: `1D 76 30 m xL xH yL yH d…` where m=0 (normal),
 * (xL,xH) = bytes-per-row (little-endian), (yL,yH) = height in dots.
 */
export function encodeRasterEscPos(
  mono: ArrayLike<number>,
  width: number,
  height: number,
): Uint8Array {
  const widthBytes = Math.ceil(width / 8);
  const data = new Uint8Array(widthBytes * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (mono[y * width + x]) {
        const idx = y * widthBytes + (x >> 3);
        data[idx] = (data[idx] ?? 0) | (0x80 >> (x & 7));
      }
    }
  }
  const header = new Uint8Array([
    0x1d,
    0x76,
    0x30,
    0x00,
    widthBytes & 0xff,
    (widthBytes >> 8) & 0xff,
    height & 0xff,
    (height >> 8) & 0xff,
  ]);
  return concat(header, data);
}

// ─── Debug / preview (Task 10.4) ──────────────────────────────────────────────

/**
 * Render an ESC/POS byte stream as readable ASCII for development + unit tests.
 * Strips the ESC/GS command sequences the templates emit (init, alignment,
 * emphasis, font size, feed-N, HRI/height/width, the `GS k` Code 128 barcode, and
 * a `GS v 0` raster logo), keeps printable ASCII (0x20–0x7E), and turns each line
 * feed into a newline. NOT a printer emulator — a preview aid only (so a barcode
 * and a logo raster appear as blank lines, since the HRI / bitmap render on the
 * device, not in this text view).
 */
export function ticketToAscii(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  const n = bytes.length;
  while (i < n) {
    const b = bytes[i] ?? 0;
    if (b === 0x1b) {
      // ESC …: ESC @ is 2 bytes; every other ESC command the templates emit
      // (ESC a/d/E/-/!/R n) is 3 bytes (opcode + 1 parameter).
      i += bytes[i + 1] === 0x40 ? 2 : 3;
    } else if (b === 0x1d) {
      const cmd = bytes[i + 1];
      if (cmd === 0x6b) {
        // GS k m n d… — Code 128 (function 73): skip opcode+m+n and n data bytes.
        i += 4 + (bytes[i + 3] ?? 0);
      } else if (cmd === 0x76) {
        // GS v 0 m xL xH yL yH d… — raster: skip the 8-byte header + the bitmap.
        const widthBytes = (bytes[i + 4] ?? 0) + (bytes[i + 5] ?? 0) * 256;
        const heightDots = (bytes[i + 6] ?? 0) + (bytes[i + 7] ?? 0) * 256;
        i += 8 + widthBytes * heightDots;
      } else {
        // GS V/H/h/w n — opcode + 1 parameter.
        i += 3;
      }
    } else if (b === 0x0a) {
      out += '\n';
      i += 1;
    } else if (b >= 0x20 && b <= 0x7e) {
      out += String.fromCharCode(b);
      i += 1;
    } else {
      // Any other control byte — skip.
      i += 1;
    }
  }
  return out;
}
